// Keep sizing in sync with the 4px inter-card gap (`gap-1`, `pb-1`) and
// the default 9px outer padding. Deck surfaces override the padding to 4px.
export const GRID_GAP = 4;
export const GRID_HORIZONTAL_PADDING = 9;
const GRID_HORIZONTAL_INSET = GRID_HORIZONTAL_PADDING * 2;
export const DEFAULT_COLUMN_COUNT = 4;
// Tile size at the default column count on a typical phone width. Wider
// containers (iPads, web viewports) add columns instead of inflating each
// tile, so a card on a 1000px viewport looks identical to a card on a 390px
// viewport — just rendered more often per row. This is the load-bearing
// invariant for the card's pure-proportional ("cqi") sizing: same `tileSize`
// → same visual render across web and native.
//
// The info is sized to the widest common phone so a phone always lands on
// `DEFAULT_COLUMN_COUNT` (4) columns — a wider Android handset must not sprout a
// 5th column. Only genuinely large surfaces (tablets, web viewports) exceed it
// and add columns.
//
// Computed with `ceil` so that at exactly
// `REFERENCE_LAYOUT_WIDTH` the column algorithm picks `DEFAULT_COLUMN_COUNT`
// rather than rounding up by one due to sub-pixel slack.
const REFERENCE_LAYOUT_WIDTH = 600;
export const MAX_TILE_SIZE = Math.ceil(
  (REFERENCE_LAYOUT_WIDTH - GRID_HORIZONTAL_INSET - GRID_GAP * (DEFAULT_COLUMN_COUNT - 1)) /
    DEFAULT_COLUMN_COUNT,
);

export function computeTileSize(
  availableWidth: number,
  columnCount: number,
  padding = GRID_HORIZONTAL_PADDING,
) {
  const gapTotal = GRID_GAP * (columnCount - 1);
  // Preserve fractional widths: rounding every tile down leaves the remainder
  // after the last column, making the right inset larger than the left.
  return Math.max(1, (availableWidth - padding * 2 - gapTotal) / columnCount);
}

export function computeColumnCount(
  availableWidth: number,
  minColumnCount: number,
  maxTileSize: number,
  padding = GRID_HORIZONTAL_PADDING,
) {
  const innerWidth = availableWidth - padding * 2;
  const minColumnsForMaxSize = Math.ceil((innerWidth + GRID_GAP) / (maxTileSize + GRID_GAP));
  return Math.max(minColumnCount, minColumnsForMaxSize);
}
